import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import * as yup from "yup";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Checkbox from "../../components/Core/Checkbox/Checkbox";
import { DropdownController } from "../../components/Core/Dropdown/Dropdown";
import TextArea from "../../components/Core/TextArea/TextArea";
import Flex from "../../components/Layout/Flex";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { routes } from "../../configs/constants/routes";
import { ViewModels } from "../../configs/constants/ViewModels";
import Contract, {
	ContractWithoutCircularRef,
	CreateOrUpdateContractRequest,
} from "../../models/Contract";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useContract, useContracts, useUpdateContract } from "../../networking/contract.hooks";
import { useAssignableInvoiceTemplates } from "../../networking/invoice-template.hooks";
import { formatRoute } from "../../utils/route.utils";
import { ContractPriceController } from "./Prices/ContractPrices";

const inputsValidationSchema = yup.object().shape({
	name: yup.string().required("FIELD_IS_REQUIRED"),
	paymentDays: yup.number().typeError("VALUE_SHOULD_INTEGER"),
	invoiceTemplate: yup.object().shape({ id: yup.string().required("FIELD_IS_REQUIRED") }),
});

const ContractInformationTab: React.FC<{ contract: Contract }> = ({ contract }) => {
	const [selectedFallbackContract, setSelectedFallbackContract] = useState(
		contract.fallbackContract?.id
	);

	const { contracts, isLoading } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		sort: CONTRACT_SORTING_COLUMN.NAME,
		pageSize: 1000,
	});

	const { t } = useTranslation();
	const { mutate: updateContract, isLoading: isUpdating } = useUpdateContract();

	// Assignable = non-archived; a contract still pointing at an archived
	// template gets a placeholder option so the select is not blank.
	const { templates: invoiceTemplates } = useAssignableInvoiceTemplates();
	const invoiceTemplateOptions = [
		...invoiceTemplates,
		...(contract.invoiceTemplate?.id && !invoiceTemplates.some((tpl) => tpl.id === contract.invoiceTemplate?.id)
			? [{ id: contract.invoiceTemplate.id, name: t("contract.archivedInvoiceTemplate") }]
			: []),
	].map((tpl) => ({ id: tpl.id, name: tpl.name }));

	const { register, handleSubmit, reset, control, formState } = useForm<ContractWithoutCircularRef>(
		{
			defaultValues: {
				fallbackContract: { id: "" },
				invoiceTemplate: { id: contract.invoiceTemplate?.id || "" },
				prices: contract.prices || [],
			},
			mode: "onBlur",
			resolver: yupResolver(inputsValidationSchema),
		}
	);

	useEffect(() => {
		reset({
			...contract,
			fallbackContract: { id: contract?.fallbackContract?.id || "" },
			invoiceTemplate: { id: contract?.invoiceTemplate?.id || "" },
			prices: contract?.prices || [],
		});
	}, [reset, contract]);

	const onSubmit = handleSubmit(async (e) => {
		const updatedContract: CreateOrUpdateContractRequest = {
			name: e.name,
			paymentDays: Number(e.paymentDays || 0),
			fallbackContract: e.fallbackContract?.id ? { id: e.fallbackContract?.id } : undefined,
			active: e.active,
			description: e.description,
			prices: e.prices?.filter((p) => !p.inherited),
			invoiceTemplate: e.invoiceTemplate?.id ? { id: e.invoiceTemplate.id } : undefined,
		};

		await updateContract({
			contractId: contract.id,
			update: updatedContract,
		});
	});

	const { contract: fallbackContract } = useContract({
		contractId: selectedFallbackContract,
		inheritFromFallback: true,
	});

	return (
		<div>
			{isLoading ? (
				<LoadingIndicator />
			) : (
				<form onSubmit={onSubmit}>
					<Flex margin="2rem 0 4rem 0" direction="column">
						<Flex maxWidth="1000px" direction="column">
							<AppInput
								{...register("name")}
								label="contract.name"
								description="contract.nameDesc"
								placeholder="NAME"
								errors={formState.errors?.name?.message}
								mandatory
								disabled={contract.isDeleted}
							/>
							<AppInput
								{...register("paymentDays")}
								type="number"
								label="contract.invoiceDays"
								description="contract.invoiceDaysDesc"
								placeholder="CONTRACT_INVOICE_DAYS_PLACEHOLDER"
								errors={formState.errors?.paymentDays?.message}
								disabled={contract.isDeleted}
							/>

							<TextArea
								{...register("description")}
								label="contract.privateDesc"
								description="contract.privateDescDesc"
								name="description"
								height="100px"
								containerMargin="0 1rem 1rem 0"
								disabled={contract.isDeleted}
							/>

							<Flex alignItems="center">
								<DropdownController<Pick<Contract, "id" | "name">>
									label="contract.fallback"
									description="contract.fallbackDesc"
									placeholder="contract.select"
									control={control}
									name="fallbackContract.id"
									getOptionLabel={(option) => option.name}
									getValue={(val) => val.id}
									options={[{ id: "", name: `- ${t("NONE")} -` }, ...(contracts || [])]}
									width="80%"
									disabled={contract.isDeleted}
									onChange={setSelectedFallbackContract}
								/>
								{selectedFallbackContract && (
									<Link
										to={formatRoute(routes.CONTRACT_DETAILS + "/" + routes.CONTRACT_INFO, [
											selectedFallbackContract,
										])}
										target="_blank"
									>
										<AppText margin="0 0 0.5rem 0.5rem" textDecoration="underline" cursor="pointer">
											{t("OPEN_IN_NEW_TAB")}
										</AppText>
									</Link>
								)}
							</Flex>

							<Flex margin="1.5rem 0 0 0" direction="column">
								<Checkbox
									{...register("active")}
									label={t("IS_ACTIVE")}
									description="contract.activeDesc"
									disabled={contract.isDeleted}
								/>
							</Flex>

							<Flex margin="1.5rem 0 1rem 0" direction="column">
								<DropdownController<{ id: string; name: string }>
									label="contract.invoiceTemplate"
									description="contract.invoiceTemplateDesc"
									placeholder="contract.selectInvoiceTemplate"
									control={control}
									name="invoiceTemplate.id"
									getOptionLabel={(option) => option.name}
									getValue={(val) => val.id}
									options={invoiceTemplateOptions}
									errors={formState.errors?.invoiceTemplate?.id?.message}
									width="80%"
									disabled={contract.isDeleted}
									mandatory
								/>
							</Flex>
						</Flex>

						{contract && !contract.isDeleted && (
							<ContractPriceController
								fallbackPrices={fallbackContract?.prices}
								control={control}
								name="prices"
								errors={formState.errors?.prices}
							/>
						)}
					</Flex>

					{!contract.isDeleted && (
						<FloatingButtonPanel transparent={false}>
							<Button width="9.75rem" loading={isUpdating}>
								{t("SAVE_CHANGES")}
							</Button>
						</FloatingButtonPanel>
					)}
				</form>
			)}
		</div>
	);
};

export default ContractInformationTab;
